import axios from 'axios';

// API Base URL - use remote backend unless overridden via VITE_API_BASE_URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://dein-ernaehrungsplan-backend-po2wyl2tv.vercel.app/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Include cookies for server-side sessions (prod nutribot.ch)
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Let routes/components handle auth state; do not mutate storage here.
    }
    return Promise.reject(error);
  }
);

export class ApiService {
  // Auth endpoints
  static async login(email: string, password: string) {
    // Legacy path kept for backward compatibility if backend provides it
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  }

  static async loginPassword(email: string, password: string) {
    // New password login endpoint
    const response = await api.post('/auth/login-password', { email, password });
    return response.data;
  }

  static async adminLogin(email: string, password: string) {
    const response = await api.post('/admin/admin-login', { email, password });
    return response.data;
  }

  static async register(userData: any) {
    const response = await api.post('/auth/register', userData);
    return response.data;
  }

  static async logout() {
    const response = await api.post('/auth/logout');
    return response.data;
  }

  // Forgot/Reset Password endpoints (updated)
  static async forgotPassword(email: string) {
    const response = await api.post('/auth/password-reset-request', { email });
    return response.data;
  }

  static async resetPassword(token: string, newPassword: string) {
    const response = await api.post('/auth/password-reset-confirm', { token, password: newPassword });
    return response.data;
  }

  // Admin endpoints
  static async getAdminStats() {
    const response = await api.get('/admin/stats');
    return response.data;
  }

  static async getAdminUsers() {
    const response = await api.get('/admin/users');
    return response.data;
  }

  static async adminUserAction(userId: string, action: 'ban' | 'activate' | 'delete') {
    const response = await api.post('/admin/users', { userId, action });
    return response.data;
  }

  static async updateAdminUser(userId: number, data: any) {
    const response = await api.put('/admin/users', { userId, ...data });
    return response.data;
  }

  static async adminCreateUser(userData: any) {
    const response = await api.post('/admin/create-user', userData);
    return response.data;
  }

  static async adminCreatePromoCode(promoData: any) {
    // Route through Stripe namespace where promo code creation lives
    const response = await api.post('/stripe/create-promo-code', promoData);
    return response.data;
  }

  static async getPromoAnalytics() {
    // Promo analytics are exposed under Stripe namespace
    const response = await api.get('/stripe/promo-analytics');
    return response.data;
  }

  static async sendBulkEmail(type: string, subject: string, content: string) {
    const response = await api.post('/admin/bulk-email', { type, subject, content });
    return response.data;
  }

  static async getNotificationStats() {
    const response = await api.get('/admin/notification-stats');
    return response.data;
  }

  // User endpoints
  static async getUserProfile() {
    const response = await api.get('/user/profile');
    return response.data;
  }

  static async updateUserProfile(data: any) {
    const response = await api.put('/user/profile', data);
    return response.data;
  }

  static async getUserMealPlan() {
    const response = await api.get('/user/meal-plan');
    const raw = response.data;

    // Normalize plan to ensure 30 days and non-empty meals
    const fallbackMeals = () => ([
      { name: 'Oatmeal with berries', foods: [], calories: 350, time: '08:00' },
      { name: 'Chicken salad', foods: [], calories: 500, time: '12:30' },
      { name: 'Grilled fish with veggies', foods: [], calories: 600, time: '19:00' },
    ]);

    const getPrefs = () => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        const allergies = Array.isArray(u?.allergies) ? u.allergies : [];
        const disliked = Array.isArray(u?.dislikedFoods) ? u.dislikedFoods : [];
        const liked = Array.isArray(u?.likedFoods) ? u.likedFoods : [];
        const restrictions = Array.isArray(u?.dietaryRestrictions) ? u.dietaryRestrictions : [];
        return { allergies, disliked, liked, restrictions };
      } catch {
        return { allergies: [], disliked: [], liked: [], restrictions: [] };
      }
    };

    const sanitizeFoods = (inFoods: any): string[] => {
      const { allergies, disliked, restrictions } = getPrefs();
      let arr: string[] = [];
      if (Array.isArray(inFoods)) arr = inFoods as string[];
      else if (typeof inFoods === 'string') arr = [inFoods];

      const normList = (list: any[]): string[] => list
        .filter((x) => typeof x === 'string')
        .map((x) => x.trim())
        .filter((x) => x.length > 0);

      const a = normList(allergies);
      const d = normList(disliked);
      const r = normList(restrictions);

      const shouldExclude = (item: string) => {
        const low = item.toLowerCase();
        const hits = (set: string[]) => set.some((t) => {
          const tt = t.toLowerCase();
          if (tt.length >= 3) return low.includes(tt);
          return low === tt;
        });
        return hits(a) || hits(d) || hits(r);
      };

      const out = normList(arr)
        .filter((f) => !shouldExclude(f));

      const seen = new Set<string>();
      const dedup = out.filter((f) => {
        const key = f.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      return dedup;
    };

    const normalizeMeals = (inMeals: any): any[] => {
      if (!Array.isArray(inMeals)) {
        if (typeof inMeals === 'string' && inMeals.trim()) {
          return [{ name: inMeals.trim(), foods: [], calories: 0, time: '' }];
        }
        return fallbackMeals();
      }
      const out = inMeals
        .map((m: any) => {
          if (typeof m === 'string') {
            const nm = m.trim();
            if (nm) return { name: nm, foods: [], calories: 0, time: '' };
            return null;
          }
          if (!m || typeof m.name !== 'string' || m.name.trim() === '') {
            return null;
          }
          return {
            name: m.name,
            foods: sanitizeFoods(m.foods),
            calories: typeof m.calories === 'number' ? m.calories : 0,
            time: typeof m.time === 'string' ? m.time : '',
          };
        })
        .filter(Boolean) as any[];
      return out.length > 0 ? out : fallbackMeals();
    };

    const baseDays: any[] = Array.isArray(raw?.days)
      ? raw.days.map((d: any, idx: number) => ({
          day: typeof d?.day === 'string' && d.day.trim().length > 0 ? d.day : `Tag ${idx + 1}`,
          meals: normalizeMeals(d?.meals),
        }))
      : [];

    const days: any[] = (() => {
      if (baseDays.length === 0) {
        return Array.from({ length: 30 }).map((_, i) => ({ day: `Tag ${i + 1}`, meals: fallbackMeals() }));
      }
      const out: any[] = [...baseDays];
      while (out.length < 30) {
        const src = baseDays[out.length % baseDays.length];
        const clone = {
          day: `Tag ${out.length + 1}`,
          meals: src?.meals ? JSON.parse(JSON.stringify(src.meals)) : fallbackMeals(),
        };
        out.push(clone);
      }
      return out.slice(0, 30);
    })();

    const normalizedPlan = {
      ...raw,
      days,
      text: typeof raw?.text === 'string' ? raw.text : '',
      totalCalories: typeof raw?.totalCalories === 'number' ? raw.totalCalories : days.reduce(
        (sum: number, dy: any) => sum + dy.meals.reduce((s: number, m: any) => s + (typeof m.calories === 'number' ? m.calories : 0), 0),
        0
      ),
      shoppingList: Array.isArray(raw?.shoppingList) ? raw.shoppingList : [],
    };

    const todaysMeals = Array.isArray(normalizedPlan?.todaysMeals) && normalizedPlan.todaysMeals.length > 0
      ? normalizeMeals(normalizedPlan.todaysMeals)
      : (days[0]?.meals || fallbackMeals());

    return { ...normalizedPlan, todaysMeals };
  }

  static async generateMealPlan(preferences: any) {
    const response = await api.post('/user/meal-plan', preferences);
    return response.data;
  }

  // CHANGE: save meal plan by sending full mealPlan object, prefer canonical endpoint
  static async saveMealPlan(mealPlan: any) {
    try {
      const response = await api.post('/user/meal-plan', mealPlan);
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 405) {
        try {
          const putResp = await api.put('/user/meal-plan', mealPlan);
          return putResp.data;
        } catch (err2: any) {
          console.warn('saveMealPlan 405; continuing without persisting');
          return { ok: false } as any;
        }
      }
      // Swallow other failures to avoid breaking UI actions (PDF/shopping list)
      return { ok: false } as any;
    }
  }

  // NutriBot endpoints
  static async sendNutriBotMessage(message: string, context?: any) {
    console.log('[NutriBot] sendNutriBotMessage -> /nutribot/chat', { messageLen: message?.length ?? 0, hasContext: !!context });
    try {
      const response = await api.post('/nutribot/chat', { message, context });
      return response.data;
    } catch (err: any) {
      // Final fallback to OpenAI if backend chat fails
      try {
        const key = import.meta.env.VITE_OPENAI_API_KEY;
        if (!key) throw err;
        const body = {
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are NutriBot, a nutritional AI agent that tailors practical, personalized guidance. Never mention ChatGPT or OpenAI; identify only as NutriBot. Avoid generic disclaimers; focus on actionable, nutrition-specific advice. Explicitly state that you can generate a PDF of the current meal plan on request; do not claim inability to create PDFs.' },
            { role: 'user', content: message },
          ],
        };
        const resp = await axios.post('https://api.openai.com/v1/chat/completions', body, {
          headers: { Authorization: `Bearer ${key}`, 'Content-Type': 'application/json' },
        });
        const content = resp?.data?.choices?.[0]?.message?.content || 'I can\'t reach the assistant right now.';
        return { reply: content };
      } catch (openaiErr) {
        console.error('[NutriBot] OpenAI fallback failed', (openaiErr as any)?.message);
        throw err;
      }
    }
  }

  // Intent detection endpoint
  static async detectIntent(message: string, conversationHistory: any[] = [], userPreferences: any = {}) {
    try {
      // Ensure backend receives userId (some routes require it explicitly)
      const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
      const uid = (userPreferences && (userPreferences.id || userPreferences._id)) || storedUser.id || storedUser._id;

      const response = await api.post('/nutribot/detect-intent', {
        message,
        conversationHistory,
        userPreferences,
        userId: uid,
      });
      return response.data;
    } catch (err: any) {
      console.error('Intent detection failed:', err.message);
      // Fallback to simple keyword detection
      const lowerMessage = message.toLowerCase();

      // Prefer access to current plan when user asks to show/view/fetch their existing plan
      const wantsCurrentPlan = (
        (/(current|existing|saved|mein|meine|my)/.test(lowerMessage) && /(meal\s*plan|ernährungsplan|ernaehrungsplan|plan)/.test(lowerMessage))
      ) || (
        (/(show|view|see|access|fetch|give me|zeige|zeigen|ansehen|abrufen)/.test(lowerMessage) && /(meal\s*plan|ernährungsplan|ernaehrungsplan|plan)/.test(lowerMessage))
      ) || lowerMessage.includes('current meal plan') || lowerMessage.includes('mein aktueller plan') || lowerMessage.includes('aktuellen ernährungsplan');
      if (wantsCurrentPlan) {
        return { intent: 'meal_plan_access', confidence: 0.8, details: { extracted_context: message, required_action: 'Fallback detection' }, response: 'Wie kann ich Ihnen helfen?' };
      }
      
      if (lowerMessage.includes('ernährungsplan') || lowerMessage.includes('meal plan') || 
          lowerMessage.includes('plan erstellen') || lowerMessage.includes('mache mir einen plan')) {
        return { intent: 'meal_plan_creation', confidence: 0.7, details: { extracted_context: message, required_action: 'Fallback detection' }, response: 'Wie kann ich Ihnen helfen?' };
      }
      if (lowerMessage.includes('pdf') || lowerMessage.includes('herunterladen') || lowerMessage.includes('download')) {
        return { intent: 'pdf_generation', confidence: 0.7, details: { extracted_context: message, required_action: 'Fallback detection' }, response: 'Wie kann ich Ihnen helfen?' };
      }
      if (lowerMessage.includes('email') || lowerMessage.includes('e-mail') || lowerMessage.includes('mail')) {
        return { intent: 'email_request', confidence: 0.7, details: { extracted_context: message, required_action: 'Fallback detection' }, response: 'Wie kann ich Ihnen helfen?' };
      }
      if (lowerMessage.includes('alte') || lowerMessage.includes('vorherige') || lowerMessage.includes('gespeichert')) {
        return { intent: 'database_access', confidence: 0.7, details: { extracted_context: message, required_action: 'Fallback detection' }, response: 'Wie kann ich Ihnen helfen?' };
      }
      
      return { intent: 'general_chat', confidence: 0.7, details: { extracted_context: message, required_action: 'Fallback detection' }, response: 'Wie kann ich Ihnen helfen?' };
    }
  }

  static async getNutriBotHistory() {
    try {
      const response = await api.get('/nutribot/history');
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        const fallback = await api.get('/nutribot/history');
        return fallback.data;
      }
      throw err;
    }
  }

  // Billing endpoints
  static async getBillingHistory() {
    const response = await api.get('/billing/history');
    return response.data;
  }

  static async createStripeSession(planType: string, billingCycle: string) {
    // Align with billing namespace to avoid 404s on production
    const response = await api.post('/billing/create-session', { planType, billingCycle });
    return response.data;
  }

  // Public endpoints
  static async getPublicStats() {
    const response = await api.get('/public/stats');
    return response.data;
  }

  // Dashboard utilities
  static async downloadMealPlanPdf() {
    try {
      // Fetch latest user and meal plan
      const userResp = await this.getUserProfile();
      const user = userResp?.user || {};
      const planResp = await this.getUserMealPlan();
      const mealPlan = planResp?.mealPlan || planResp || {};

      // Provide userId explicitly for backend PDF generator
      const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
      const uid = user?.id || user?._id || storedUser?.id || storedUser?._id;

      // Call robust backend PDF generator using current plan
      const gen = await api.post('/nutribot/generate-pdf', {
        mealPlan,
        user: { id: user.id, name: user.name, email: user.email },
        userId: uid,
        store: 'dashboard'
      });
      const downloadUrl: string = gen?.data?.downloadUrl;

      if (!downloadUrl) {
        throw new Error('No downloadUrl from generator');
      }

      // Convert data URL to Blob
      if (downloadUrl.startsWith('data:application/pdf;base64,')) {
        const base64 = downloadUrl.replace('data:application/pdf;base64,', '');
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: 'application/pdf' });
      }

      // Fallback: fetch URL to get blob
      const resp = await axios.get(downloadUrl, { responseType: 'blob' });
      return resp.data;
    } catch (err: any) {
      console.warn('downloadMealPlanPdf failed, falling back to placeholder endpoint:', err?.message);
      const response = await api.get('/user/meal-plan/pdf', { responseType: 'blob' });
      return response.data; // blob
    }
  }

  static async generateShoppingList() {
    try {
      const response = await api.post('/user/meal-plan/shopping-list');
      const list = Array.isArray(response?.data?.shoppingList) ? response.data.shoppingList : [];
      const normalized = list.map((it: any) => {
        const name = typeof it === 'string' ? it : (it?.name || it?.label || it?.text || '');
        return {
          name,
          label: name,
          text: name,
          unit: typeof it === 'object' ? (it.unit || it.units || undefined) : undefined,
          amount: typeof it === 'object' ? (Number(it.amount || it.qty || 0) || undefined) : undefined,
        };
      });
      return { shoppingList: normalized };
    } catch (err: any) {
      if (err?.response?.status === 405) {
        const fallback = await api.get('/user/meal-plan/shopping-list');
        const list = Array.isArray(fallback?.data?.shoppingList) ? fallback.data.shoppingList : [];
        const normalized = list.map((it: any) => {
          const name = typeof it === 'string' ? it : (it?.name || it?.label || it?.text || '');
          return {
            name,
            label: name,
            text: name,
            unit: typeof it === 'object' ? (it.unit || it.units || undefined) : undefined,
            amount: typeof it === 'object' ? (Number(it.amount || it.qty || 0) || undefined) : undefined,
          };
        });
        return { shoppingList: normalized };
      }
      throw err;
    }
  }

  static async planNextWeek(preferences?: any) {
    try {
      const response = await api.post('/user/meal-plan/next-week', preferences || {});
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 405) {
        const fallback = await api.get('/user/meal-plan/next-week');
        return fallback.data;
      }
      throw err;
    }
  }

  static async updatePreferences(preferences: any) {
    const response = await api.put('/user/preferences', preferences);
    return response.data;
  }

  static async sendPlanEmail(email: string, mealPlan: any) {
    const response = await api.post('/user/meal-plan/email', { email, mealPlan });
    return response.data;
  }

  static async markMealCompleted(mealId: string | number) {
    try {
      // Read current completion count
      const statusResp = await api.get('/user/meal-plan/complete');
      const current = Number(statusResp?.data?.daysCompleted || 0);
      const next = current + 1;
      const incrementStreak = next % 4 === 0; // assume 4 meals per day
      const putResp = await api.put('/user/meal-plan/complete', { daysCompleted: next, incrementStreak });
      return putResp.data;
    } catch (err: any) {
      // Fallbacks for older backends
      try {
        const putResp = await api.put('/user/meal-plan/complete', { daysCompleted: 1, incrementStreak: false });
        return putResp.data;
      } catch (err2: any) {
        if (err2?.response?.status === 405) {
          const getResp = await api.get(`/user/meal-plan/complete`, { params: { mealId } });
          return getResp.data;
        }
        throw err2;
      }
    }
  }

  static async getUserInsights() {
    try {
      const response = await api.get('/user/insights');
      return response.data;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        return { insights: [] };
      }
      throw err;
    }
  }

  // Generate AI insights on backend, falling back to OpenAI if unavailable
  static async generateAiInsights(payload: any) {
    try {
      const response = await api.post('/user/insights/generate', payload);
      return response.data;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 501) {
        const key = import.meta.env.VITE_OPENAI_API_KEY;
        if (!key) return { insights: [] };
        const meals = payload?.meals || [];
        const totals = payload?.totals || {};
        const summary = `Totals: calories=${totals.calories}, protein=${totals.protein}g, carbs=${totals.carbs}g, fats=${totals.fats}g. Meals: ${meals
          .map((m: any) => `${m.name} (${m.calories} kcal, P${m.protein} C${m.carbs} F${m.fats})`)
          .join('; ')}`;
        const body = {
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are a helpful nutrition assistant. Provide concise insights and suggestions.' },
            { role: 'user', content: `Provide 3 short insights for this daily plan: ${summary}` },
          ],
          temperature: 0.4,
        };
        const resp = await axios.post('https://api.openai.com/v1/chat/completions', body, {
          headers: { Authorization: `Bearer ${key}`, 'Content-Type': 'application/json' },
        });
        const text: string = resp?.data?.choices?.[0]?.message?.content || '';
        const insights = text
          .split(/\n+/)
          .map((s: string) => s.replace(/^[-•]\s*/, '').trim())
          .filter((s: string) => s.length)
          .slice(0, 3)
          .map((msg: string, i: number) => ({
            title: i === 0 ? 'Daily Summary' : i === 1 ? 'Optimization Tip' : 'Hydration/Balance',
            message: msg,
            type: i === 0 ? 'info' : i === 1 ? 'suggestion' : 'success',
            icon: 'ri-sparkling-2-line',
          }));
        return { insights };
      }
      throw err;
    }
  }

  static async requestEmailVerification(email: string) {
    const response = await api.post('/auth/verify-request', { email });
    return response.data;
  }
  
  static async confirmEmailVerification(email: string, otp: string) {
    const response = await api.post('/auth/verify-confirm', { email, otp });
    return response.data;
  }
}

export default ApiService;